import { Box, Href, Text } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/DayPicker'
import DropdownSearchBar from 'modules/core/components/DropdownSearchBar'
import LabeledFancyCheckbox from 'modules/core/components/Inputs/LabeledFancyCheckbox'
import TextArea from 'modules/core/components/Inputs/TextArea'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { stringToDate } from 'modules/core/lib/dates'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import { ContractFlowType } from 'modules/contracts/components/FlowPage'
import FormWrapper from 'modules/contracts/components/FlowPage/FormWrapper'
import Hint from 'modules/contracts/components/FlowPage/FormWrapper/Hint'
import { useContractFlow } from 'modules/contracts/lib/contractFlowContext'
import { getFirstError } from 'modules/contracts/lib/errors'
import { translate } from 'modules/contracts/lib/translate'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import SpainLegalAndExtras from './SpainLegalAndExtras'
import { Authentication as A3innuvaAuthentication } from './SpainLegalAndExtras/A3innuvaFields'
import ContractTypeSelect from './_ContractTypeSelect'
import CustomFields from './_CustomFields'
import ForfaitJourLegalAndExtras from './_ForfaitJourLegalAndExtras'
import FranceLegalAndExtras from './_FranceLegalAndExtras'
import GermanyLegalAndExtras from './_GermanyLegalAndExtras'
import { stepHasErrors } from './validator'

interface Props {
  form: Form
  translations: { [key: string]: string }
  step: number
  type: ContractFlowType
  setIsCurrentStepCompleted: (isCompleted: boolean) => void
}

interface ContractTypeHintProps {
  legalEntityId: number
  step: number
  type: ContractFlowType
}

const ContractTypeHint = ({ legalEntityId, step, type }: ContractTypeHintProps) => {
  const { navigation } = useNavigation()

  const trackLink = () => {
    track(trackingEvents.employee.contracts.goToContractType, {
      step: step,
      type: type,
    })
  }

  return (
    <Hint color="grey200">
      <Href
        onClick={() => trackLink()}
        href={navigation.settings.legalEntitiesId.contracts.fullPath({ id: legalEntityId })}
        target="_blank"
      >
        <Text color="grey1000" weight="normal" size="100" underlined>
          {i18n.t('contract_versions.flows.fix.steps.legal_and_extras.hints.contract_type')}
          <Box paddingLeft="s4" style={{ display: 'inline' }}>
            <Text color="grey1000" weight="normal" size="100" displayContents>
              {i18n.t(
                'contract_versions.flows.fix.steps.legal_and_extras.hints.contract_type_link'
              )}
            </Text>
          </Box>
        </Text>
      </Href>
    </Hint>
  )
}

const LegalAndExtras = ({ form, translations, step, type, setIsCurrentStepCompleted }: Props) => {
  const { employee, contractVersion, compensations, initialCompensations, contractVersionPolicy } =
    useContractFlow()
  const endsOnField = form.get('ends_on')
  const trialPeriodEndField = form.has('trial_period_ends_on')
    ? form.get('trial_period_ends_on')
    : undefined
  const trialPeriodEndValue = trialPeriodEndField ? trialPeriodEndField.value : ''
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    setIsCurrentStepCompleted(
      !stepHasErrors(
        form,
        contractVersion,
        compensations,
        initialCompensations,
        contractVersionPolicy
      )
    )
  }, [endsOnField.value, trialPeriodEndValue, form.dirtyFieldsKeys.join()])
  /* eslint-enable react-hooks/exhaustive-deps */

  const trialPeriodOptions = [
    {
      value: 1,
      label: i18n.t('contract_versions.forms.basic_information.yes'),
    },
    {
      value: 0,
      label: i18n.t('contract_versions.forms.basic_information.no'),
    },
  ]

  const trialPeriodName = () => {
    const currentOption = trialPeriodOptions.find(
      (e) => !!e.value === form.get('has_trial_period').value
    )
    return currentOption?.label || `-- ${i18n.t('common.none')} --`
  }

  const canSeeContractType: boolean = policyAuthorizations.for(
    'contracts.see_contract_type',
    employee
  )
  const canSeeTrialPeriod: boolean = policyAuthorizations.for(
    'contracts.see_trial_period',
    employee
  )
  const canSeeTrialEnd: boolean = policyAuthorizations.for(
    'contracts.see_trial_period_end_date',
    employee
  )
  const legalEntity = employeeLegalEntity(employee)
  const isSpanish =
    contractVersion && type === 'fix' ? contractVersion.isSpanish : legalEntity.country === 'es'
  const isFrench =
    contractVersion && type === 'fix' ? contractVersion.isFrench : legalEntity.country === 'fr'
  const isItalian = contractVersion && type === 'fix' && legalEntity.country === 'it'
  const isGerman =
    contractVersion && type === 'fix' ? contractVersion.isGerman : legalEntity.country === 'de'
  const isPortuguese =
    contractVersion && type === 'fix' ? contractVersion.isPortuguese : legalEntity.country === 'pt'
  const hasContractType = isSpanish || isFrench || isGerman || isPortuguese
  const hasTrialPeriod = form.has('has_trial_period')

  const legalEntityCountry = legalEntity.attributes.country
  const isForfaitJourEntity = (isFrench && legalEntityCountry === 'fr') || isItalian
  const hasTTworkableDaysDefinition = hasFeature('dev_time_tracking_workable_days_definition')

  return (
    <Box gap="s32" paddingBottom="s40">
      <Box gap="s8">
        <FormWrapper>
          <Text size="400" weight="medium" color="grey1000">
            {translate(translations, 'header')}
          </Text>
        </FormWrapper>
      </Box>
      {legalEntityCountry === 'es' && isSpanish && (
        <A3innuvaAuthentication companyId={legalEntity.attributes.company_id} />
      )}
      <Box gap="s24">
        {!hasTTworkableDaysDefinition && canSeeContractType && hasContractType && (
          <FormWrapper
            hint={<ContractTypeHint legalEntityId={legalEntity.id} step={step} type={type} />}
          >
            <ContractTypeSelect form={form} type={type} />
          </FormWrapper>
        )}
        {legalEntityCountry === 'es' && isSpanish && (
          <FormWrapper>
            <LabeledFancyCheckbox
              field={form.get('es_has_teleworking_contract')}
              label={translate(translations, 'teleworking')!}
            />
          </FormWrapper>
        )}
        {form.has('ends_on') && (
          <FormWrapper>
            <DayPicker
              withNull
              value={endsOnField.value}
              error={getFirstError(endsOnField)}
              onChange={(value) => endsOnField.set(value)}
              label={translate(translations, 'end_date')}
              minDate={stringToDate(form.get('effective_on').value, 'ui')}
              withNullLabel={translate(translations, 'end_date_none')}
            />
          </FormWrapper>
        )}
        {hasTrialPeriod && canSeeTrialPeriod && (
          <FormWrapper>
            <Box gap="s24">
              <DropdownSearchBar
                name={trialPeriodName()}
                label={translate(translations, 'trial_period') || ''}
                onSelect={(item) => {
                  form.get('has_trial_period').set(!!item.value)
                }}
                labelNotFound={i18n.t('contract_versions.forms.basic_information.option_not_found')}
                data={trialPeriodOptions}
                searchLabel={i18n.t(
                  'contract_versions.forms.contract_extra_information.a3innuva.search_label'
                )}
                popoverWidth="s400"
              />
              {canSeeTrialEnd && form.get('has_trial_period').value && (
                <DayPicker
                  value={trialPeriodEndField!.value}
                  error={getFirstError(trialPeriodEndField!)}
                  onChange={(value) => trialPeriodEndField!.set(value)}
                  label={translate(translations, 'trial_period_end')}
                  minDate={stringToDate(form.get('effective_on').value, 'ui')}
                />
              )}
            </Box>
          </FormWrapper>
        )}
        {legalEntityCountry === 'es' && isSpanish && (
          <SpainLegalAndExtras form={form} translations={translations} employee={employee} />
        )}
        {legalEntityCountry === 'fr' && isFrench && (
          <FranceLegalAndExtras form={form} translations={translations} employee={employee} />
        )}
        {isForfaitJourEntity && (
          <ForfaitJourLegalAndExtras form={form} translations={translations} />
        )}
        {legalEntityCountry === 'de' && isGerman && (
          <GermanyLegalAndExtras form={form} translations={translations} employee={employee} />
        )}
        {legalEntityCountry === 'es' && isSpanish && (
          <FormWrapper>
            <TextArea
              field={form.get('es_contract_observations')}
              label={translate(translations, 'contract_observations')}
            />
          </FormWrapper>
        )}
        <CustomFields form={form} translations={translations} step={step} type={type} />
      </Box>
    </Box>
  )
}

export default observer(LegalAndExtras)
